<template>
  <section class="section-drama">
    <div class="tech-bg">
      <div class="grid-overlay"></div>
      <div class="floating-particles">
        <div class="particle" v-for="n in 30" :key="n" :style="particleStyle()"></div>
      </div>
      <div class="glow-orb glow-orb-1"></div>
      <div class="glow-orb glow-orb-2"></div>
      <div class="glow-orb glow-orb-3"></div>
      <div class="hex-grid"></div>
    </div>
    <div class="container">
      <div class="page-head">
        <h2>AI 文案创作</h2>
        <p>智能生成各类营销文案、宣传语、社交媒体内容</p>
      </div>
      <div class="card">
      <div class="tab-container">
          <button
            class="tab-btn"
            :class="{active: activeTab === 'general'}"
            @click="activeTab = 'general'"
          >
            通用文案
          </button>
          <button
            class="tab-btn"
            :class="{active: activeTab === 'ecommerce'}"
            @click="activeTab = 'ecommerce'"
          >
            电商营销
          </button>
          <button
            class="tab-btn"
            :class="{active: activeTab === 'project'}"
            @click="activeTab = 'project'"
          >
            项目方案
          </button>
          <button
            class="tab-btn"
            :class="{active: activeTab === 'improve'}"
            @click="activeTab = 'improve'"
          >
            文案改进
          </button>
          <button
            class="tab-btn"
            :class="{active: activeTab === 'summary'}"
            @click="activeTab = 'summary'"
          >
            总结报告
          </button>
        </div>

      <div class="tab-content">
        <div class="form-group">
          <label>关键词（用逗号分隔）</label>
          <input
            type="text"
            v-model="form.keywords"
            placeholder="请输入关键词，如：优惠、品质、科技"
            class="input"
          >
        </div>

        <!-- 文件上传区域 -仅在文案改进和总结报告显示 -->
        <div class="form-group" v-if="activeTab === 'improve' || activeTab === 'summary'">
          <label>上传文档</label>
          <div class="upload-area" @click="triggerFileUpload">
            <input 
              type="file" 
              ref="fileInput" 
              accept=".doc,.docx,.pdf,.txt,.md"
              @change="handleFileUpload" 
              style="display: none"
            >
            <div v-if="!form.documentName" class="upload-placeholder">
              <div class="upload-icon">📁</div>
              <div class="upload-text">点击上传文档</div>
              <div class="upload-hint">支持 Word、PDF、TXT、Markdown 格式</div>
            </div>
            <div v-else class="uploaded-file">
              <div class="file-icon">📄</div>
              <div class="file-info">
                <p class="file-name">{{ form.documentName }}</p>
              </div>
              <button class="remove-btn" @click.stop="removeDocument">×</button>
            </div>
          </div>
        </div>

        <div class="form-group">
          <label>字数要求</label>
          <select v-model="form.length" class="select">
            <option v-for="option in lengthOptions" :key="option.key" :value="option.key">
              {{ option.label }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label>选择模型</label>
          <div class="model-select-container">
            <div class="selected-model" @click="toggleModelDropdown">
              <img v-if="form.model && models[form.model]?.image_url" :src="models[form.model]?.image_url" :alt="models[form.model]?.name" class="model-icon">
              <span v-if="form.model && models[form.model]">{{ models[form.model]?.name }}</span>
              <span v-else>选择模型</span>
              <span class="dropdown-arrow">▼</span>
            </div>
            <div v-if="modelDropdownOpen" class="model-dropdown">
              <div class="model-group">
                <div class="model-group-title">🌟 免费模型</div>
                <div v-for="(model, identifier) in freeModelsObj" :key="identifier" class="model-item" @click="selectModel(identifier)">
                  <img v-if="model.image_url" :src="model.image_url" :alt="model.name" class="model-icon">
                  <span class="model-name">{{ model.name }}</span>
                  <div class="model-tags">
                    <span v-if="model.is_new" class="model-tag new">NEW</span>
                    <span v-if="model.is_hot" class="model-tag hot">HOT</span>
                    <span v-if="model.discount > 0" class="model-tag discount">{{ model.discount }}% OFF</span>
                  </div>
                </div>
              </div>
              <div class="model-group" v-if="Object.keys(paidModelsObj).length > 0">
                <div class="model-group-title">💎 付费模型</div>
                <div v-for="(model, identifier) in paidModelsObj" :key="identifier" class="model-item" @click="selectModel(identifier)">
                  <img v-if="model.image_url" :src="model.image_url" :alt="model.name" class="model-icon">
                  <span class="model-name">{{ model.name }}</span>
                  <div class="model-tags">
                    <span v-if="model.is_new" class="model-tag new">NEW</span>
                    <span v-if="model.is_hot" class="model-tag hot">HOT</span>
                    <span v-if="model.discount > 0" class="model-tag discount">{{ model.discount }}% OFF</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="model-hint" v-if="form.model && models[form.model]">
            <span :class="['model-type', models[form.model].model_type]">
              {{ models[form.model].model_type === 'free' ? '免费' : '付费' }}
            </span>
            <span v-if="models[form.model].description" class="model-desc">{{ models[form.model].description }}</span>
          </div>
        </div>

        <div class="form-group">
          <label>风格要求</label>
          <select v-model="form.style" class="select">
            <option v-for="(style, key) in styleOptions" :key="key" :value="style.name">
              {{ style.name }}
            </option>
          </select>
        </div>

        <button
          class="btn primary"
          @click="generateContent"
          :disabled="loading"
        >
          {{ loading ? '创作中...' : '生成文案' }}
        </button>

        <div v-if="result" class="result-container">
          <div class="result-header">
            <h3>创作结果</h3>
            <button
              v-if="result.content"
              class="btn-copy"
              @click="copyContent"
            >
              {{ copySuccess ? '已复制 ✓' : '一键复制' }}
            </button>
          </div>
          <div class="result-content">
            <pre>{{ result.content }}</pre>
          </div>
          <div class="result-actions">
            <button class="btn secondary" @click="regenerate">重新生成</button>
            <button class="btn secondary" @click="saveToHistory">保存到历史</button>
          </div>
        </div>
      </div>
    </div>
  </div>
  </section>
</template>

<script>
import axios from 'axios'
import { authMixin } from '../utils/authMixin'
import appCache from '../utils/appCache'

export default {
    name: 'TextGenerator',
    mixins: [authMixin],
    data() {
        return {
            activeTab: 'general',
            form: {
                keywords: '',
                length: 'medium',
                style: '',
                model: '',
                documentName: '',
                documentUrl: '',
                documentFile: null
            },
            models: {},
            modelDropdownOpen: false,
            lengthOptions: [
                { key: 'short', label: '短文案（50~100）', min: 50, max: 100 },
                { key: 'medium', label: '中文案（100~500）', min: 100, max: 500 },
                { key: 'long', label: '长文案（500以上）', min: 500, max: 9999 }
            ],
            styleOptions: {},
            result: null,
            loading: false,
            copySuccess: false
        }
    },
  mounted() {
    this.initPage()
  },
  computed: {
    freeModelsObj() {
      const free = {}
      Object.entries(this.models).forEach(([key, model]) => {
        if (model.model_type === 'free' || model.model_type === undefined) {
          free[key] = model
        }
      })
      return free
    },
    paidModelsObj() {
      const paid = {}
      Object.entries(this.models).forEach(([key, model]) => {
        if (model.model_type === 'paid') {
          paid[key] = model
        }
      })
      return paid
    }
  },
  methods: {
    particleStyle() {
      const size = Math.random() * 4 + 1
      const left = Math.random() * 100
      const top = Math.random() * 100
      const duration = Math.random() * 20 + 10
      const delay = Math.random() * 10
      return {
        width: `${size}px`,
        height: `${size}px`,
        left: `${left}%`,
        top: `${top}%`,
        animationDuration: `${duration}s`,
        animationDelay: `${delay}s`
      }
    },
    async initPage() {
      // 先加载后台配置，确保默认模型优先
      await this.loadTextConfig()
      // 然后加载模型列表
      await this.loadModels()
    },
    async loadTextConfig() {
      try {
        const config = await appCache.getTextConfig()
        if (config) {
          this.lengthOptions = config.lengthOptions || this.lengthOptions
          this.styleOptions = config.styles || {}
          // 设置默认模型
          if (config.model) {
            this.form.model = config.model
          }
          if (Object.keys(this.styleOptions).length > 0) {
            const firstStyleKey = Object.keys(this.styleOptions)[0]
            this.form.style = this.styleOptions[firstStyleKey].name
          }
        }
      } catch (error) {
        console.error('加载文案配置失败:', error)
        // 默认值
        this.form.model = 'gpt-3.5-turbo'
      }
    },
    async loadModels() {
      try {
        const textModels = await appCache.getModelsByCategory('text')
        this.models = {}
        textModels.forEach(model => {
          this.models[model.identifier] = model
        })
        console.log('从缓存加载的文本模型:', this.models)
        // 如果还没有设置模型（配置中没有设置），使用第一个可用模型
        if (!this.form.model && Object.keys(this.models).length > 0) {
          this.form.model = Object.keys(this.models)[0]
        }
      } catch (error) {
        console.error('加载模型失败:', error)
        // 默认模型
        this.models = {
          'gpt-3.5-turbo': { name: 'GPT-3.5 Turbo', is_hot: true, is_new: false },
          'gpt-4': { name: 'GPT-4', is_hot: true, is_new: false },
          'claude-3.5-sonnet': { name: 'Claude 3.5 Sonnet', is_hot: false, is_new: false }
        }
        if (!this.form.model) {
          this.form.model = 'gpt-3.5-turbo'
        }
      }
    },
    
    triggerFileUpload() {
      this.$refs.fileInput.click()
    },
    
    toggleModelDropdown() {
      this.modelDropdownOpen = !this.modelDropdownOpen
    },
    
    selectModel(identifier) {
      this.form.model = identifier
      this.modelDropdownOpen = false
    },
    
    async handleFileUpload(event) {
      const file = event.target.files[0]
      if (!file) return
      
      this.form.documentName = file.name
      this.form.documentFile = file
      
      try {
        const formData = new FormData()
        formData.append('file', file)
        
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
        if (userInfo && userInfo.id) {
          formData.append('user_id', userInfo.id)
        }
        
        const response = await axios.post('/api/document-upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        
        if (response.data.code === 200) {
          this.form.documentUrl = response.data.data.document_url
          alert('文档上传成功！')
        } else {
          alert('文档上传失败: ' + (response.data.message || '未知错误'))
          this.removeDocument()
        }
      } catch (error) {
        console.error('文档上传失败:', error)
        alert('文档上传失败，请重试')
        this.removeDocument()
      }
    },
    
    removeDocument() {
      this.form.documentName = ''
      this.form.documentUrl = ''
      this.form.documentFile = null
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },
    
    async generateContent() {
      if (!this.form.keywords || this.form.keywords.trim() === '') {
        alert('请输入关键词')
        return
      }
      
      if ((this.activeTab === 'improve' || this.activeTab === 'summary') && !this.form.documentUrl) {
        alert('文案改进和总结报告需要先上传参考文档')
        return
      }
      
      const query = {
        prompt: this.form.keywords,
        type: this.activeTab,
        model: this.form.model,
        wordCount: this.form.length,
        style: this.form.style
      }
      
      if (this.form.documentUrl) {
        query.documentUrl = this.form.documentUrl
        query.documentName = this.form.documentName
      }
      
      this.$router.push({
        path: '/text-generator-pro',
        query: query
      })
    },
    
    async copyContent() {
        if (this.result && this.result.content) {
            try {
                await navigator.clipboard.writeText(this.result.content)
                this.copySuccess = true
                setTimeout(() => {
                    this.copySuccess = false
                }, 2000)
            } catch (error) {
                console.error('复制失败:', error)
                alert('复制失败')
            }
        }
    },
    
    regenerate() {
        this.result = null
        this.generateContent()
    },
    
    saveToHistory() {
        if (this.result) {
            alert('已保存到历史记录')
        }
    }
  }
}
</script>

<style scoped>
.section-drama {
  padding-top: 100px;
  padding-bottom: 60px;
  box-sizing: border-box;
  background: linear-gradient(180deg, #0a0e1a 0%, #0d1224 50%, #0a0e1a 100%);
  position: relative;
  min-height: 100vh;
}

.tech-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

.grid-overlay {
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(rgba(0, 230, 172, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 230, 172, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: gridMove 20s linear infinite;
}

@keyframes gridMove {
  0% { transform: translate(0, 0); }
  100% { transform: translate(50px, 50px); }
}

.floating-particles {
  position: absolute;
  width: 100%;
  height: 100%;
}

.particle {
  position: absolute;
  background: rgba(0, 230, 172, 0.4);
  border-radius: 50%;
  animation: floatParticle linear infinite;
  filter: blur(1px);
  box-shadow: 0 0 8px rgba(0, 230, 172, 0.3);
}

@keyframes floatParticle {
  0% { transform: translateY(0) translateX(0); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(-100vh) translateX(50px); opacity: 0; }
}

.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.15;
}

.glow-orb-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(0, 204, 153, 0.6) 0%, transparent 70%);
  top: 20%;
  left: 10%;
  animation: floatOrb1 15s ease-in-out infinite;
}

.glow-orb-2 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(0, 230, 172, 0.6) 0%, transparent 70%);
  top: 50%;
  right: 15%;
  animation: floatOrb2 18s ease-in-out infinite;
}

.glow-orb-3 {
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, rgba(79, 172, 254, 0.6) 0%, transparent 70%);
  bottom: 10%;
  left: 50%;
  animation: floatOrb3 20s ease-in-out infinite;
}

@keyframes floatOrb1 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(100px, -50px); }
}

@keyframes floatOrb2 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-80px, 80px); }
}

@keyframes floatOrb3 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-60px, -100px); }
}

.hex-grid {
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='49' viewBox='0 0 28 49'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%2300e6ac' fill-opacity='0.02'%3E%3Cpath d='M13.99 9.25l13 7.5v15l-13 7.5L1 31.75v-15l12.99-7.5zM3 17.9v12.7l10.99 6.34 11-6.35V17.9l-11-6.34L3 17.9zM0 15l12.98-7.5V0h-2v6.35L0 12.69v2.3zm0 18.5L12.98 41v8h-2v-6.85L0 35.81v-2.3zM15 0v7.5L27.99 15H28v-2.31h-.01L17 6.35V0h-2zm0 49v-8l12.99-7.5H28v2.31h-.01L17 42.15V49h-2z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.4;
}

.container {
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-head {
  text-align: center;
  margin-bottom: 40px;
}

.page-head h2 {
  font-size: 32px;
  margin-bottom: 10px;
}

.page-head p {
  color: #888;
  font-size: 16px;
}

.card {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 16px;
  padding: 40px;
  max-width: 900px;
  margin: 0 auto;
}

.tab-container {
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  padding-bottom: 10px;
  flex-wrap: wrap;
}

.tab-btn {
  padding: 12px 24px;
  background: transparent;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: #aaa;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.tab-btn:hover {
  background: rgba(255,255,255,0.05);
  color: #fff;
}

.tab-btn.active {
  background: linear-gradient(135deg, #00e6ac, #00cc99);
  border-color: #00e6ac;
  color: #000;
  font-weight: 600;
}

.tab-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #aaa;
}

.input, .select {
  width: 100%;
  padding: 12px 16px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.input:focus, .select:focus {
  outline: none;
  border-color: #00e6ac;
  background: rgba(0,230,172,0.05);
}

.select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23aaa' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 40px;
}

.select option {
  background: #1a1e2d;
  color: #fff;
  padding: 10px;
}

.upload-area {
  width: 100%;
  padding: 40px 20px;
  background: rgba(255,255,255,0.05);
  border: 2px dashed rgba(255,255,255,0.2);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  box-sizing: border-box;
}

.upload-area:hover {
  border-color: #00e6ac;
  background: rgba(0,230,172,0.05);
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.upload-icon {
  font-size: 48px;
}

.upload-text {
  color: #fff;
  font-size: 16px;
}

.upload-hint {
  color: #888;
  font-size: 14px;
}

.uploaded-file {
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: center;
}

.file-icon {
  font-size: 40px;
}

.file-info {
  flex: 1;
  text-align: left;
}

.file-name {
  color: #fff;
  font-size: 16px;
  margin: 0;
}

.remove-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255,107,107,0.2);
  border: 1px solid rgba(255,107,107,0.4);
  color: #ff6b6b;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.remove-btn:hover {
  background: rgba(255,107,107,0.3);
  transform: scale(1.1);
}

.btn {
  padding: 12px 32px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn.primary {
  background: linear-gradient(135deg, #00e6ac, #00cc99);
  color: #000;
  width: 100%;
}

.btn.primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,230,172,0.3);
}

.btn.primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn.secondary {
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  color: #aaa;
}

.btn.secondary:hover {
  background: rgba(255,255,255,0.1);
  color: #fff;
}

.progress-container {
  margin-top: 30px;
}

.progress-bar {
  height: 8px;
  background: rgba(255,255,255,0.1);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00e6ac, #00cc99);
  border-radius: 4px;
  transition: width 0.3s ease;
  position: relative;
}

.progress-wave {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: linear-gradient(45deg, rgba(255,255,255,0.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.15) 75%, transparent 75%, transparent);
  background-size: 40px 40px;
  animation: wave 1s linear infinite;
}

@keyframes wave {
  from { background-position: 0 0; }
  to { background-position: 40px 0; }
}

.progress-text {
  text-align: center;
  margin-top: 12px;
  color: #888;
  font-size: 14px;
}

.result-container {
  margin-top: 30px;
  padding: 24px;
  background: rgba(0,230,172,0.05);
  border: 1px solid rgba(0,230,172,0.2);
  border-radius: 12px;
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.result-header h3 {
  font-size: 18px;
  color: #00e6ac;
  margin: 0;
}

.btn-copy {
  padding: 8px 20px;
  background: linear-gradient(135deg, #00e6ac, #00cc99);
  border: none;
  border-radius: 6px;
  color: #000;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-copy:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,230,172,0.3);
}

.result-content {
  margin-bottom: 20px;
}

.result-content pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  color: #fff;
  font-size: 15px;
  line-height: 1.8;
  margin: 0;
  font-family: inherit;
}

/* 模型类型样式 */
.model-type {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  margin-right: 8px;
}

.model-type.free {
  background: rgba(0, 230, 172, 0.1);
  color: #00e6ac;
}

.model-type.paid {
  background: rgba(255, 152, 0, 0.1);
  color: #ff9800;
}

/* 模型标签样式 */
.model-tag {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  margin-left: 4px;
  vertical-align: middle;
}

.model-tag.new {
  background: linear-gradient(135deg, #00e6ac, #00cc99);
  color: #000;
}

.model-tag.hot {
  background: linear-gradient(135deg, #ff6b6b, #ee5a6f);
  color: #fff;
}

.model-tag.discount {
  background: linear-gradient(135deg, #ff9800, #f57c00);
  color: #fff;
}

/* 模型选择下拉菜单样式 */
.model-select-container {
  position: relative;
  width: 100%;
}

.selected-model {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.selected-model:hover {
  border-color: #00e6ac;
  background: rgba(0, 230, 172, 0.05);
}

.model-select-container .model-icon {
  width: 24px;
  height: 24px;
  margin-right: 10px;
  border-radius: 4px;
}

.dropdown-arrow {
  margin-left: auto;
  font-size: 12px;
  color: #999;
  transition: transform 0.3s ease;
}

.model-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #1a1e2d;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-top: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
}

.model-group {
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.model-group:last-child {
  border-bottom: none;
}

.model-group-title {
  padding: 0 15px 10px;
  font-size: 14px;
  font-weight: 600;
  color: #00e6ac;
}

.model-item {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.model-item:hover {
  background: rgba(0, 230, 172, 0.1);
}

.model-item .model-name {
  flex: 1;
  margin-right: 10px;
}

.model-tags {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.model-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #888;
}

.model-hint .hint-tag {
  margin-left: 8px;
}

.model-hint .hint-tag.hot {
  color: #ff6b6f;
}

.model-hint .hint-tag.new {
  color: #00e6ac;
}

.model-desc {
  margin-left: 12px;
  font-size: 13px;
  color: #999;
  line-height: 1.4;
}

.result-actions {
  display: flex;
  gap: 12px;
}
</style>
